import React, { useState } from 'react';
import { BarChart3, ArrowLeft, HelpCircle, Play, Trophy, Zap } from 'lucide-react';
import { useApp } from './AppContext';
import { YandexRTB } from './YandexRTB';
import type { Screen } from '../App';

interface StatisticsScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const StatisticsScreen: React.FC<StatisticsScreenProps> = ({ onNavigate, onBack }) => {
  const {
    userStats,
    getBackgroundClass
  } = useApp();



  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Header */}
      <div className="app-header-fixed flex items-center">
        <button
          onClick={onBack}
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text truncate">Статистика</h1>
        </div>
        <button
          onClick={() => onNavigate('statistics-help')}
          className="ml-2 sm:ml-3 arrow-button flex-shrink-0"
          aria-label="Помощь"
        >
          <HelpCircle className="arrow-icon" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">

        {/* Overall Stats */}
        <div className="mb-4 sm:mb-6 md:mb-8 glass-panel rounded-xl">
          <div className="p-3 sm:p-4 md:p-6">
            <h2 className="font-medium glass-text mb-3 sm:mb-4 md:mb-6 text-sm sm:text-base md:text-lg landscape-compact-text-xs landscape:text-xs">Общая статистика</h2>

            {/* Mobile layout: stacked vertically on very small screens */}
            <div className="block xs:hidden mb-3 sm:mb-4 md:mb-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm glass-text-secondary">Всего ответов</span>
                  <span className="text-sm sm:text-base font-bold glass-text">{userStats.totalAnswered}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm glass-text-secondary">Правильных</span>
                  <span className="text-sm sm:text-base font-bold text-emerald-300">{userStats.correctAnswers}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm glass-text-secondary">Неправильных</span>
                  <span className="text-sm sm:text-base font-bold text-rose-300">{userStats.incorrectAnswers}</span>
                </div>
              </div>
            </div>

            {/* Small screens and up: grid layout */}
            <div className="hidden xs:grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-6 text-center mb-3 sm:mb-4 md:mb-6">
              <div className="min-w-0">
                <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-sm landscape:text-base font-bold glass-text">{userStats.totalAnswered}</div>
                <div className="text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape:text-xs glass-text-secondary">Всего ответов</div>
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-sm landscape:text-base font-bold text-emerald-300">{userStats.correctAnswers}</div>
                <div className="text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape:text-xs glass-text-secondary">Правильных</div>
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-sm landscape:text-base font-bold text-rose-300">{userStats.incorrectAnswers}</div>
                <div className="text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape:text-xs glass-text-secondary">Неправильных</div>
              </div>
            </div>

            {userStats.totalAnswered > 0 && (
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between text-sm sm:text-base md:text-lg landscape-compact-text-xs landscape:text-sm glass-text">
                  <span>Общая точность</span>
                  <span>{Math.round((userStats.correctAnswers / userStats.totalAnswered) * 100)}%</span>
                </div>
                <div className="w-full glass-progress-bg rounded-full h-2 sm:h-3 md:h-4 landscape-compact-h-2 landscape:h-2 overflow-hidden">
                  <div
                    className="glass-progress-fill h-full rounded-full transition-all duration-500"
                    style={{ width: `${(userStats.correctAnswers / userStats.totalAnswered) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Time Stats */}
        <div className="mb-4 sm:mb-6 md:mb-8 glass-panel rounded-xl">
          <div className="p-3 sm:p-4 md:p-6 flex flex-col justify-center">
            {/* Заголовок и общее время в одной строке */}
            <div className="flex justify-between items-center mb-3 sm:mb-4 md:mb-6">
              <h2 className="font-medium glass-text text-sm sm:text-base md:text-lg landscape-compact-text-xs landscape:text-xs">Время вашего обучения</h2>
              <span className="font-medium glass-text text-sm sm:text-base md:text-lg landscape-compact-text-xs landscape:text-sm">
                {(() => {
                  const totalMinutes = (userStats.studyTimeMinutes || 0) + (userStats.examTimeMinutes || 0) + (userStats.marathonTimeMinutes || 0);
                  if (totalMinutes === 0) return '0мин';
                  return totalMinutes >= 60
                    ? `${Math.floor(totalMinutes / 60)}ч ${totalMinutes % 60}мин`
                    : `${totalMinutes}мин`;
                })()}
              </span>
            </div>

            {/* Детализация по типам тестов - показываем только если есть время */}
            {((userStats.studyTimeMinutes || 0) > 0 || (userStats.examTimeMinutes || 0) > 0 || (userStats.marathonTimeMinutes || 0) > 0) && (
              <div className="space-y-1 sm:space-y-2 border-t border-white/20 pt-2 sm:pt-3">
                {(userStats.studyTimeMinutes || 0) > 0 && (
                  <div className="flex justify-between text-sm sm:text-base landscape-compact-text-xs landscape:text-sm glass-text">
                    <span className="flex items-center gap-2">
                      <Play className="h-4 w-4 glass-icon" />
                      Подготовка
                    </span>
                    <span>
                      {userStats.studyTimeMinutes >= 60
                        ? `${Math.floor(userStats.studyTimeMinutes / 60)}ч ${userStats.studyTimeMinutes % 60}мин`
                        : `${userStats.studyTimeMinutes}мин`
                      }
                    </span>
                  </div>
                )}

                {(userStats.examTimeMinutes || 0) > 0 && (
                  <div className="flex justify-between text-sm sm:text-base landscape-compact-text-xs landscape:text-sm glass-text">
                    <span className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 glass-icon" />
                      Экзамены
                    </span>
                    <span>
                      {userStats.examTimeMinutes >= 60
                        ? `${Math.floor(userStats.examTimeMinutes / 60)}ч ${userStats.examTimeMinutes % 60}мин`
                        : `${userStats.examTimeMinutes}мин`
                      }
                    </span>
                  </div>
                )}

                {(userStats.marathonTimeMinutes || 0) > 0 && (
                  <div className="flex justify-between text-sm sm:text-base landscape-compact-text-xs landscape:text-sm glass-text">
                    <span className="flex items-center gap-2">
                      <Zap className="h-4 w-4 glass-icon" />
                      Марафоны
                    </span>
                    <span>
                      {userStats.marathonTimeMinutes >= 60
                        ? `${Math.floor(userStats.marathonTimeMinutes / 60)}ч ${userStats.marathonTimeMinutes % 60}мин`
                        : `${userStats.marathonTimeMinutes}мин`
                      }
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Category Stats */}
        {Object.keys(userStats.categoryStats).length > 0 && (
          <div className="mb-4 sm:mb-6 md:mb-8 glass-panel rounded-xl">
            <div className="p-3 sm:p-4 md:p-6">
              <h2 className="font-medium glass-text mb-3 sm:mb-4 md:mb-6 text-sm sm:text-base md:text-lg landscape-compact-text-xs landscape:text-xs">Статистика по категориям</h2>
              <div className="space-y-2 xs:space-y-3 sm:space-y-4 md:space-y-5 landscape-compact-space-y-1 landscape:space-y-2">
                {Object.entries(userStats.categoryStats).map(([category, stats]) => {
                  const total = stats.correct + stats.incorrect;
                  const percentage = total > 0 ? Math.round((stats.correct / total) * 100) : 0;

                  return (
                    <div key={category} className="space-y-1 xs:space-y-2 sm:space-y-3 landscape-compact-space-y-1 landscape:space-y-1">
                      <div className="flex justify-between items-center gap-2">
                        <span className="font-medium glass-text text-sm sm:text-base md:text-lg landscape-compact-text-xs landscape:text-sm flex-1 min-w-0 truncate">{category}</span>
                        <div className={`flex-shrink-0 ${percentage >= 80
                            ? 'glass-badge-success'
                            : percentage >= 60
                              ? 'glass-badge-warning'
                              : 'glass-badge-danger'
                          }`}>
                          {percentage}%
                        </div>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base landscape-compact-text-xs landscape:text-sm glass-text-secondary">
                        <span>Правильных: {stats.correct}</span>
                        <span>Неправильных: {stats.incorrect}</span>
                      </div>
                      <div className="w-full glass-progress-bg rounded-full h-2 sm:h-3 landscape-compact-h-2 landscape:h-2 overflow-hidden">
                        <div
                          className="glass-progress-fill h-full rounded-full"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {userStats.totalAnswered === 0 && (
          <div className="glass-panel rounded-xl shadow-lg">
            <div className="p-4 xs:p-6 sm:p-6 landscape-compact-p-3 landscape:p-4 text-center">
              <h3 className="font-medium glass-text mb-1 text-base sm:text-lg md:text-xl landscape-compact-text-sm landscape:text-base">
                Начните тестирование
              </h3>
              <p className="text-sm sm:text-base landscape-compact-text-xs landscape:text-sm glass-text-secondary">
                Пройдите несколько тестов, чтобы увидеть детальную статистику.
              </p>
            </div>
          </div>
        )}

        {/* Нативный рекламный блок (показывается всегда после всех информационных блоков) */}
        <div className="w-full mt-4 sm:mt-6 mb-4 sm:mb-6 flex justify-center rounded-xl bg-white/5 min-h-[100px]">
          <YandexRTB type="native" idMobile="R-A-17579918-7" idDesktop="R-A-17579918-7" />
        </div>
      </div>
    </div>
  );
};